import React, { Component } from 'react';
import { connect } from 'react-redux';
import { every, takeWhile } from 'lodash';
import { Button, Typography } from 'rmwc';
import { debounce } from 'lodash';
import { Text } from '../../components/Text';
import { LoadingBar } from '../../components/util/LoadingBar';
import {
  MTAssumptionsSection,
  MTCTAv2,
  MTDivider,
  MTHeader,
  MTLearnMore,
  MTLearnMoreItem,
  MTScorePanel,
  MTSkinny,
  MTWide,
  MTCalc,
  MTSubcontent,
  MTSubcontentH1,
  MTSubcontentH2,
  MTSubcontentHR,
  MTSubcontentLabel,
  MTSubcontentP,
  MTSubheading,
} from './layout';
import GuaranteedIncomeAssumptions from './GuaranteedIncomeAssumptions';
import LetterScore from '../../components/charts/LetterScore';
import { currencyFormatter, Money } from '../../components/Money';
import { withRegistrationDialog } from '../../components/RegistrationDialog';
import { withEmailDialog } from '../../components/EmailDialog';
import { runFullSimulation, setPlanImpactUpdatedPath } from '../../actions/simulationActions';
import { fetchGuaranteedIncome, fetchSpendingSourcesGuaranteedIncome } from '../../actions/wealthManagementActions';
import { fetchSpendingPlan } from '../../actions/wealthManagementActions';
import { fetchJobsModule } from '../../actions/jobsActions';
import { fetchPeople } from '../../actions/peopleActions';
import { updateScoreFromSimulations } from '../../actions/toolsActions';

import GuaranteedIncomeChart from '../../components/charts/GuaranteedIncomeChart';
import GuaranteedIncomeSidebar from './GuaranteedIncomeSidebar';
import { wlPath, wlVar } from '../../helpers/whitelabel';

const LIFE_EXPECTANCY_DEFAULT = 93;

export class GuaranteedIncome extends Component {
  constructor(props) {
    super(props);

    this.state = {
      diaConversion: 0,
      financialShock: 'none',
      planToAge: props.primaryPerson ? this.getPlanToAge(props.primaryPerson) : 0,
      graphData: [],
    };

    this.delayOnParamsChanged = debounce(this.onParamsChanged.bind(this), 300);
  }

  getPlanToAge = person => {
    if (person && person.life_expectancy) {
      return person.life_expectancy;
    } else {
      return 93;
    }
  };

  onParamsChanged = () => {
    this.props.fetchSpendingSourcesGuaranteedIncome(
      this.state.diaConversion,
      this.state.financialShock,
      this.state.planToAge
    );
    // this.props.runFullSimulation('DASHBOARD_', {
    //   dia_conversion: this.state.diaConversion,
    //   plan_to_age: this.state.planToAge,
    // });
  };

  onChange = (key, value) => {
    if (this.state[key] != value) {
      this.setState({ [key]: value }, this.delayOnParamsChanged);
    }
  };

  componentDidMount() {
    // if (!this.props.results) {
    //   this.props.runFullSimulation();
    // }
    if (!this.props.people) {
      this.props.fetchPeople();
    }
    if (!this.props.jobs) {
      this.props.fetchJobsModule();
    }
    this.props.fetchGuaranteedIncome();
    this.props.fetchSpendingPlan();
    this.props.fetchSpendingSourcesGuaranteedIncome(this.state.diaConversion, this.state.financialShock);
  }

  componentWillReceiveProps = nextProps => {
    if (nextProps.spendingSources && nextProps.spendingSources[undefined] !== this.props.spendingSources[undefined]) {
      const graphData = nextProps.spendingSources[undefined].map(data => ({
        year: data.year,
        age: data.age,
        data: [['savings', data.savings], ['other', data.other], ['social', data.social]],
      }));

      const startIndex = takeWhile(graphData, ({ data }) => every(data, row => row[1] === 0)).length;

      this.setState({ graphData: graphData, startIndex: startIndex });
    }

    if (nextProps.primaryPerson && this.state.planToAge == 0) {
      this.setState({ planToAge: this.getPlanToAge(nextProps.primaryPerson) });
    }

    if (nextProps.updatedResults) {
      if (
        !this.props.updatedResults ||
        nextProps.updatedResults.scores.retirement_spending_power !=
          this.props.updatedResults.scores.retirement_spending_power
      ) {
        const { updateScoreFromSimulations } = this.props;
        updateScoreFromSimulations('Fee Savings', nextProps.jobs, nextProps.results, nextProps.updatedResults);
      }
    }
  };

  render() {
    const {
      history,
      guaranteedIncome,
      jobs,
      people,
      results,
      spendingSources,
      spendingPlan,
      primaryPerson,
    } = this.props;
    const { graphData } = this.state;

    if (!jobs || !people || !results || !guaranteedIncome || !spendingPlan) {
      return <LoadingBar />;
    }

    let planToAge = this.state.planToAge;
    if (planToAge == 0) {
      planToAge = this.getPlanToAge(primaryPerson);
    }

    let start = 0;
    if (graphData) {
      for (const [i, y] of graphData.entries()) {
        if (y.data.map(x => x[1]).reduce((a, b) => a + b, 0) != 0) {
          start = i;
          break;
        }
      }
    }

    let lifeExpectancyYear =
      new Date().getFullYear() -
      this.props.people[0].age +
      (this.props.people[0].life_expectancy || LIFE_EXPECTANCY_DEFAULT);

    let totalExtraIncome = 0;
    let retirementSpendingPowerDiff = 0;
    if (this.props.updatedResults) {
      const lifeExpectancy = this.props.people[0].life_expectancy || LIFE_EXPECTANCY_DEFAULT;
      const retirementAge = this.props.jobs.primary_retirement_age || 67;

      retirementSpendingPowerDiff =
        this.props.updatedResults.scores.retirement_spending_power -
        this.props.results.scores.retirement_spending_power;

      totalExtraIncome = retirementSpendingPowerDiff * (lifeExpectancy - retirementAge);
    }

    return (
      <div>
        <MTHeader
          title="Get Guaranteed Income to Cover Essentials"
          onClick={() => history.push(wlPath('/action-plan'))}
        />
        <MTSkinny>
          <MTSubheading title="How to guarantee cash for the things you can't live without" />
          <MTSubcontent noCard>
            <MTSubcontentP>
              As you approach retirement, you'll need to turn your nest egg into a retirement "paycheck" that
              consistently covers your essential expenses.
            </MTSubcontentP>
            <MTSubcontentP>
              One strategy is to use part of your nest egg to buy an "income guarantee." Doing this locks in a monthly
              paycheck that is guaranteed for as long as you live, no matter what happens in the market.
            </MTSubcontentP>
            <MTSubcontentP last>
              <strong>Guarantee your essential expenses, no matter what happens.</strong>
            </MTSubcontentP>
          </MTSubcontent>
        </MTSkinny>
        <MTDivider />
        <MTWide>
          <MTSubheading title="How much could Guaranteed Income change your retirement?" />

          <MTCalc
            title="Guaranteed Income Allocation"
            left={
              <div>
                <MTSubcontentLabel>
                  Use the slider to set the percent of your nest egg you put into guaranteed income
                </MTSubcontentLabel>
                <GuaranteedIncomeSidebar onChange={this.onChange} diaConversion={this.state.diaConversion} />
                <MTSubcontentHR />
                <MTSubcontentH2 title="Your retirement spending power by source of income" />

                <GuaranteedIncomeChart
                  startIndex={0}
                  data={graphData.slice(start)}
                  budgetLine={spendingPlan.retirement_budget * 12}
                />
              </div>
            }
            right={
              <MTAssumptionsSection title="Calculator assumptions" titleStyle="subheading2">
                <GuaranteedIncomeAssumptions
                  onChange={this.onChange}
                  financialShock={this.state.financialShock}
                  planToAge={planToAge}
                />
              </MTAssumptionsSection>
            }
          />
        </MTWide>
        <MTSkinny>
          <Text use="caption" className="text-muted">
            These calculators are designed to be informational and educational tools only, and when used alone, do not constitute investment advice. We strongly recommend that you seek the advice of a financial services professional before making any type of investment. We also encourage you to review your investment strategy periodically as your financial circumstances change. This model is provided as a rough approximation of future financial performance. The results presented by this calculator are hypothetical and may not reflect the actual growth of any investments. {wlVar('brand_name')} is not responsible for the consequences of any decisions or actions taken in reliance upon or as a result of the information provided by these tools.
          </Text>
        </MTSkinny>
        <MTDivider />
        <MTSkinny>
          <MTSubheading title="Start designing your retirment income strategy today" />
          <MTCTAv2
            title="Get a free retirement income consult."
            subtitle="Take just 30 minutes to guarantee your retirement"
            image={require('images/outcome-fees.png')}
            description={
              "We'll discuss guaranteed income, how it works, when to use it, and how to balance it with your other sources of income in retirement."
            }
            buttonTextSecondary="Save for later"
            onClickSecondary={() =>
              this.props.openRegistrationDialog(() => (window.location = '/start'), () => {}, 'SAVE_AND_REVISIT')
            }
            buttonTextPrimary="Schedule free consult"
            onClickPrimary={() => {}}
            talkingPointsHeading="Things we'll do during your income analysis"
            talkingPointSubheading="Here are some tips to get you started"
            talkingPointsImage={require('images/icon-situation')}
            talkingPoints={[
              {
                image: require('images/outcome-fees.png'),
                label: 'Answer your questions about how guaranteed income works.',
                text: (
                  <div className="p-3">
                    <Text use="body1" tag="p" className="pb-1">
                      Guaranteed income is an annuity. You’ll enter into a contract with an insurance company in which
                      you agree to pay a “premium” today in exchange for the insurance company’s agreeing to a
                      contractual obligation to pay you a stream of income starting in the future and continuing for the
                      rest of your life.
                    </Text>
                    <Text use="body1" tag="p" className="pb-1">
                      You will be guaranteed to always get at least your money back in payouts to you. If you live into
                      old age, you could get many multiples of your money paid back to you.
                    </Text>
                    <Text use="body1" tag="p" className="pb-1">
                      The contract makes sure that the insurance company has a legally recorded obligation to pay you
                      your money, and regulators watch to make sure the insurance company is going to be around to
                      fulfill its payments to you. In the past, even in instances where insurance companies have
                      over-promised about the guaranteed income they can pay, other insurance companies have stepped in
                      to make sure the contracts are fulfilled.
                    </Text>
                    <Text use="body1" tag="p" className="pb-1">
                      Guaranteed income, annuity contracts, are among the safest more reliable options available for
                      your money, if used correctly.  Guarantees are subject to the claims paying ability of the issuing insurance company.
                    </Text>
                  </div>
                ),
              },
              {
                image: require('images/icon_investments.png'),
                label: 'Decide if guaranteed income makes sense for you.',
                text: (
                  <div className="p-3">
                    <Text use="body1" tag="p" className="pb-1">
                      The biggest factors when it comes to guaranteed income are your goals, your life expectancy, and
                      your investment preferences. Goals determine whether or not guaranteed income fits into the
                      overall picture for your financial and retirement health. In some cases, where stable income is
                      most important, guaranteed income is a natural fit. In other cases, like when you want to leave
                      more behind to your children, it may not be so important. Your life expectancy determines whether
                      or not an investment in guaranteed income is going to pay you back enough on your money to be
                      worth it. In some situations, your investment preferences may call for more risk than guaranteed
                      income provides, or may show that you don’t need guaranteed income because you are covering your
                      income needs in other ways. All of these factors take careful weighting against one another to
                      determine if and how much guaranteed income makes sense for you.
                    </Text>
                  </div>
                ),
              },
              {
                image: require('images/icon-options.png'),
                label: 'Design a combined income strategy that includes other income sources.',
                text: (
                  <div className="p-3">
                    <Text use="body1" tag="p" className="pb-1">
                      For a number of reasons, you probably shouldnt use all of your money for guaranteed income. In
                      general, creating income in retirement is going to be about a combination of guaranteed income,
                      fixed income, Social Security, part-time work, and more. As this picture comes into sharper focus,
                      you can design an income strategy that minimizes taxes, protects against downside disruption, but
                      also leaves space for growth.
                    </Text>
                  </div>
                ),
              },
            ]}
          />
        </MTSkinny>
        <MTDivider />
        <MTSkinny>
          <MTSubheading title="Learn more" />
          <MTLearnMore>
            <MTLearnMoreItem title="Why would you not want to use Guaranteed Income?">
              <Text use="body1" tag="p" className="pb-1">
                Guaranteed income gives you peace of mind that you've covered all your essential, everyday expenses.
                However, it's not perfect for everything.
              </Text>
              <Text use="body1" tag="p" className="pb-0" style={{ fontStyle: 'italic' }}>
                <strong>Lack of Flexibility</strong>
              </Text>
              <Text use="body1" tag="p" className="pb-1">
                You can't "take back" money put into guaranteed income, so you won't be able to change your mind about
                how you want to use that money later.
              </Text>
              <Text use="body1" tag="p" className="pb-0" style={{ fontStyle: 'italic' }}>
                <strong>Inflation Protection</strong>
              </Text>
              <Text use="body1" tag="p" className="pb-1">
                Guaranteed income doesn't increase with inflation, so you have to plan your income needs relative to
                future purchasing power.
              </Text>
              <img src={require('../../images/guaranteed-income-chart.png')} width="100%" />
              <Text use="body1" tag="p" className="pt-1 pb-1">
                It's a good idea to combine income from multiple sources to create a diversified income stream in
                retirement. We can design a combined income strategy during our <a>free initial consult</a>!
              </Text>
            </MTLearnMoreItem>
          </MTLearnMore>
        </MTSkinny>
      </div>
    );
  }
}

function getPrimaryPerson(people, dashboard) {
  if (!people.fetched || !dashboard.fetched) {
    return null;
  }
  for (const person of people.data) {
    if (person.id == dashboard.data.primary_person_id) {
      return person;
    }
  }
}

function mapStateToProps(state) {
  return {
    jobs: state.modules.jobs.data,
    people: state.people.data,
    results: state.simulations.results,
    guaranteedIncome: state.modules.guaranteedIncome.data,
    spendingPlan: state.modules.spendingPlan.data,
    spendingSources: state.modules.guaranteedIncome && state.modules.guaranteedIncome.spendingSources,
    updatedResults: state.modules.dashboard.simulation ? state.modules.dashboard.simulation.results : null,
    people: state.people.data,
    primaryPerson: getPrimaryPerson(state.people, state.modules.dashboard),
  };
}

export default connect(mapStateToProps, {
  fetchJobsModule,
  fetchSpendingSourcesGuaranteedIncome,
  fetchGuaranteedIncome,
  fetchPeople,
  setPlanImpactUpdatedPath,
  runFullSimulation,
  fetchSpendingPlan,
  fetchPeople,
  updateScoreFromSimulations,
})(withEmailDialog(withRegistrationDialog(GuaranteedIncome)));
